import React, { useRef } from 'react';

import { IS_MOBILE } from 'src/helpers/environment';

import { useScrollableStyles } from './use-scrollable-styles';
import { Scrollbar } from '../Scrollbar';

export interface UseScrollableOptions {
  /** The ID of the scrollable DOM element, required for accessibility. */
  id: string;
  /**
   * Whether the horizontal and vertical scrollbars should be visible they're static and the
   * scrollable is not overflowing in their direction. By default, the scrollbars are hidden in this
   * situation, but it may be desireable to make them always visible so that the layout does not
   * shift depending on the height of the content.
   */
  visibleWhenNotOverflowing?: { horizontal?: boolean; vertical?: boolean };
}

/**
 * Add custom scrollbars to a scrollable DOM element. The DOM structure must match the example
 * below.
 *
 * @example
 *   const id = useId();
 *   const { classes, scrollableInnerRef, scrollableRef, scrollbarElements } = useScrollable({ id });
 *   return (
 *     <div className={classes.root}>
 *       <div ref={scrollableRef} className={classes.scrollable} id={id}>
 *         <div ref={scrollableInnerRef} className={classes.scrollableInner}>
 *           ...
 *         </div>
 *       </div>
 *       {scrollbarElements}
 *     </div>
 *   )
 */
export function useScrollable(options: UseScrollableOptions) {
  const { id, visibleWhenNotOverflowing = {} } = options;
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const scrollableInnerRef = useRef<HTMLDivElement | null>(null);
  const {
    classes: { bottomRightSquare, ...classes },
  } = useScrollableStyles();

  // TODO: The custom scrollbar is broken on mobile (ENG-5219)
  const scrollbarElements = !IS_MOBILE && (
    <>
      <Scrollbar
        direction="vertical"
        scrollableId={id}
        scrollableInnerRef={scrollableInnerRef}
        scrollableRef={scrollableRef}
        visibleWhenNotOverflowing={visibleWhenNotOverflowing.vertical}
      />

      <Scrollbar
        direction="horizontal"
        scrollableId={id}
        scrollableInnerRef={scrollableInnerRef}
        scrollableRef={scrollableRef}
        visibleWhenNotOverflowing={visibleWhenNotOverflowing.horizontal}
      />

      <div className={bottomRightSquare} />
    </>
  );

  return { classes, scrollableInnerRef, scrollableRef, scrollbarElements };
}
